/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { Fragment } from 'react'
import { ComboboxOption } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { Company } from 'types/entities/Company'
import { decodeHtmlEntities } from 'utils/companyUtils'

interface CompanyAutocompleteItemProps {
    company: Company
    selectedOptionRef: React.RefObject<HTMLLIElement | null>
}

const CompanyAutocompleteItem = (props: CompanyAutocompleteItemProps) => {
    const { company, selectedOptionRef } = props

    return (
        <ComboboxOption key={company.id} value={company} as={Fragment}>
            {({ selected, active }) => (
                <li
                    ref={selected ? selectedOptionRef : null}
                    className={clsx(
                        'CompanyAutocompleteItem relative flex flex-row cursor-pointer select-none py-1.5 pr-0 text-sm text-gray-900 dark:text-gray-100',
                        active || selected
                            ? 'bg-gray-200/80 text-black dark:bg-gray-800 dark:text-gray-100'
                            : ''
                    )}
                >
                    {selected && (
                        <span className={clsx('w-9 flex items-center')}>
                            <CheckIcon
                                className={clsx('w-4.5 h-4.5 grow ml-0')}
                            />
                        </span>
                    )}
                    <div className={clsx('flex flex-col', !selected && 'pl-9')}>
                        <span className={clsx('block truncate font-medium')}>
                            {`${company.id} • ${decodeHtmlEntities(company.name)}`}
                        </span>
                        <span
                            className={clsx(
                                'block truncate text-xs text-gray-500 dark:text-gray-400'
                            )}
                        >
                            {[
                                company.city,
                                company.postal_code,
                                company.street_address,
                                company.state,
                            ]
                                .filter((v) => !!v && `${v}`.trim().length > 0)
                                .join(' • ')}
                        </span>
                        <span
                            className={clsx(
                                'block truncate text-xs text-gray-500 dark:text-gray-400'
                            )}
                        >
                            {[
                                [company.first_name, company.last_name]
                                    .filter(
                                        (v) => !!v && `${v}`.trim().length > 0
                                    )
                                    .join(' '),
                                company.email,
                                company.phone,
                                company.cin,
                            ]
                                .filter((v) => !!v && `${v}`.trim().length > 0)
                                .join(' • ')}
                        </span>
                    </div>
                </li>
            )}
        </ComboboxOption>
    )
}

export default CompanyAutocompleteItem
